/**
 * Aparência dos pontos de qualidade do ar — funções puras (testadas em
 * `ar.test.ts`).
 *
 * Dois símbolos, nenhum deles já usado no mapa:
 * - **hexágono**: sensor de baixo custo (PurpleAir) da rede do Acre;
 * - **losango** com anel claro: estação oficial do MonitorAr.
 *
 * As sedes são círculos, os pluviômetros gotas, os focos chamas, os rios
 * círculos com seta; o hexágono é a forma das "colmeias" de sensores e o
 * losango se destaca dele mesmo em 6 px. A cor é a da faixa na escala da
 * rede de origem (`faixas.ts`). Sem dado no quadro, o símbolo fica vazado e tracejado —
 * nunca pintado de "boa" por falta de medição —; parado há mais de 48 h, menor
 * e mais apagado ainda.
 */
import { COR_SEM_DADO, corDaFaixa, type Faixa } from './faixas';

export type Forma = 'hexagono' | 'losango';
export type Estado = 'comDado' | 'semDadoNoQuadro' | 'parado';

export interface Visual {
  forma: Forma;
  estado: Estado;
  faixa: Faixa | null;
  /** Raio do símbolo em px. */
  raio: number;
  preenchimento: string | null;
  contorno: string;
  larguraContorno: number;
  tracejado: readonly number[];
  opacidade: number;
  /** Anel claro por fora (estação oficial). */
  anel: boolean;
}

export function formaDe(tipo: 'sensor' | 'estacao'): Forma {
  return tipo === 'sensor' ? 'hexagono' : 'losango';
}

export function visualDe(tipo: 'sensor' | 'estacao', faixa: Faixa | null, parado: boolean, tamanho: number, opacidade: number): Visual {
  const forma = formaDe(tipo);
  // O losango "encolhe" visualmente perto do hexágono de mesmo raio: 15% a mais.
  const base = forma === 'losango' ? tamanho * 1.15 : tamanho;
  if (parado) {
    return {
      forma,
      estado: 'parado',
      faixa: null,
      raio: base * 0.6,
      preenchimento: null,
      contorno: COR_SEM_DADO,
      larguraContorno: 1.1,
      tracejado: [2, 2],
      opacidade: opacidade * 0.55,
      anel: false,
    };
  }
  if (faixa === null) {
    return {
      forma,
      estado: 'semDadoNoQuadro',
      faixa: null,
      raio: base * 0.8,
      preenchimento: null,
      contorno: COR_SEM_DADO,
      larguraContorno: 1.3,
      tracejado: [2, 2],
      opacidade: opacidade * 0.8,
      anel: false,
    };
  }
  return {
    forma,
    estado: 'comDado',
    faixa,
    raio: base,
    preenchimento: corDaFaixa(faixa),
    // Contorno escuro: sem ele o verde e o amarelo somem sobre nuvem branca.
    contorno: 'rgba(8,13,24,0.9)',
    larguraContorno: 1.3,
    tracejado: [],
    opacidade,
    anel: forma === 'losango',
  };
}

/**
 * Chave do cache de estilos: uma aparência por forma, faixa (de qualquer
 * escala) e estado sem cor, por tamanho — poucas dezenas; o teste trava.
 */
export function chaveEstilo(v: Visual, dpr: number): string {
  return [dpr.toFixed(2), v.forma, v.estado, v.faixa ?? '-', v.raio.toFixed(2), v.opacidade.toFixed(3)].join('|');
}

/** Pontos (em unidades do raio) do contorno do símbolo, começando no topo. */
export function contornoDaForma(forma: Forma): [number, number][] {
  const lados = forma === 'hexagono' ? 6 : 4;
  // Hexágono com vértice em cima ("pontudo"), losango com vértice em cima.
  return Array.from({ length: lados }, (_, k) => {
    const a = -Math.PI / 2 + (k * 2 * Math.PI) / lados;
    return [Math.cos(a), Math.sin(a)] as [number, number];
  });
}
